import React, { useCallback, useEffect, useState } from 'react';
import PipelineHealthPanels from './PipelineHealthPanels';
import Pool from './Pool';
import './HealthTab.css';

// Thresholds for the week verdict (tune later if needed):
// - Solid: posted >= 5 and hit rate >= 40%
// - Needs attention: posted >= 5 and hit rate < 25%
// - Quiet / Mixed: otherwise

const fmtPct = (v) => (v == null ? '–' : `${Number(v).toFixed(1)}%`);
const fmtNum = (v) => (v == null ? '–' : Number(v).toLocaleString());

function hitClass(pct) {
  if (pct == null) return '';
  if (pct >= 40) return 'health-hit-good';
  if (pct >= 25) return 'health-hit-mid';
  return 'health-hit-low';
}

function computeVerdict(funnel, characters) {
  const posted = funnel?.posted ?? 0;
  const engaged = funnel?.engaged ?? 0;
  const hitRate = posted > 0
    ? (funnel.engaged_pct_of_posted != null
      ? Number(funnel.engaged_pct_of_posted)
      : (100 * engaged) / posted)
    : null;

  let key = 'quiet';
  let label = 'Quiet week';
  let blurb = 'Not much reply volume yet — check back after more posts land.';

  if (posted >= 5 && hitRate != null && hitRate >= 40) {
    key = 'solid';
    label = 'Solid week';
    blurb = 'Replies are landing with the audience.';
  } else if (posted >= 5 && hitRate != null && hitRate < 25) {
    key = 'attention';
    label = 'Needs attention';
    blurb = 'Posts are going out, but few are getting likes or reposts.';
  } else if (posted >= 5 && hitRate != null) {
    key = 'quiet';
    label = 'Mixed week';
    blurb = 'Some replies are connecting; room to improve hit rate.';
  }

  const cold = (characters || [])
    .filter((c) => (c.posted || 0) >= 3 && (c.hit_rate_pct == null || c.hit_rate_pct < 25))
    .map((c) => c.name);

  return { key, label, blurb, posted, engaged, hitRate, cold };
}

function WeekVerdict({ data, poolSummary, days }) {
  if (!data?.funnel) {
    return (
      <div className="health-section">
        <div className="health-empty">Waiting for reply stats…</div>
      </div>
    );
  }

  const v = computeVerdict(data.funnel, data.characters);
  const reviewCount =
    (poolSummary?.pending_demotions || 0) + (poolSummary?.mining_candidates || 0);
  const scoutLast24 = data.diversification?.scout_last_24h_stored;
  const scoutDaily = data.diversification?.scout_daily || [];
  const recentScoutZero = scoutDaily.slice(-3).filter((d) => (d.stored || 0) === 0).length >= 3;

  const chips = [];
  if (reviewCount > 0) {
    chips.push({
      key: 'review',
      className: '',
      text: `${reviewCount} account${reviewCount === 1 ? '' : 's'} waiting to review`,
    });
  }
  if (recentScoutZero || scoutLast24 === 0) {
    chips.push({
      key: 'scout',
      className: 'quiet-scout',
      text: 'Scout quiet lately',
    });
  }
  v.cold.forEach((name) => {
    chips.push({
      key: `cold-${name}`,
      className: 'cold',
      text: `${name} cold`,
    });
  });

  return (
    <div className="health-section">
      <div className="health-section-header">
        <h3>This week</h3>
        <span className="health-section-sub">Last {days} days · hit rate = any like or repost</span>
      </div>
      <div className={`health-verdict-label ${v.key}`}>{v.label}</div>
      <p className="health-verdict-blurb">{v.blurb}</p>
      <div className="health-stat-row">
        <div className="health-stat">
          <div className="health-stat-num">{fmtNum(v.posted)}</div>
          <div className="health-stat-label">Posted</div>
        </div>
        <div className="health-stat">
          <div className="health-stat-num">{fmtNum(v.engaged)}</div>
          <div className="health-stat-label">Got a reaction</div>
        </div>
        <div className="health-stat">
          <div className={`health-stat-num ${hitClass(v.hitRate)}`}>{fmtPct(v.hitRate)}</div>
          <div className="health-stat-label">Hit rate</div>
        </div>
      </div>
      {chips.length > 0 && (
        <div className="health-chips">
          {chips.map((c) => (
            <span key={c.key} className={`health-chip ${c.className}`}>{c.text}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function WhoIsLanding({ characters, days }) {
  if (!characters || characters.length === 0) {
    return (
      <div className="health-section">
        <div className="health-section-header"><h3>Who&apos;s landing</h3></div>
        <div className="health-empty">No posts in this window.</div>
      </div>
    );
  }

  const sorted = [...characters].sort((a, b) => (b.hit_rate_pct || 0) - (a.hit_rate_pct || 0));

  return (
    <div className="health-section">
      <div className="health-section-header">
        <h3>Who&apos;s landing</h3>
        <span className="health-section-sub">{days}d · sorted by hit rate</span>
      </div>
      <table className="health-char-table">
        <thead>
          <tr>
            <th>Character</th>
            <th className="num">Posted</th>
            <th className="num">Hit rate</th>
            <th className="num">Likes</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => (
            <tr key={c.name}>
              <td><strong>{c.name}</strong></td>
              <td className="num">{fmtNum(c.posted)}</td>
              <td className={`num ${hitClass(c.hit_rate_pct)}`}>{fmtPct(c.hit_rate_pct)}</td>
              <td className="num">{fmtNum(c.total_likes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Merged Funnel + Pool view.
 * Fetches /api/admin/pipeline-health; Pool loads its own queues.
 */
const HealthTab = ({ apiUrl, poolSummary = {} }) => {
  const [days, setDays] = useState(7);
  const [pipelineHealth, setPipelineHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const adminToken = localStorage.getItem('admin_jwt');
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/pipeline-health?days=${days}`, {
        headers,
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Health fetch failed (${res.status})`);
      setPipelineHealth(await res.json());
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, days]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60 * 1000);
    return () => clearInterval(interval);
  }, [refresh]);

  return (
    <div className="health-root">
      <div className="health-header">
        <h2>Health</h2>
        <div className="health-time-selector">
          <button
            type="button"
            className={days === 7 ? 'active' : ''}
            onClick={() => setDays(7)}
          >
            Last 7 days
          </button>
          <button
            type="button"
            className={days === 30 ? 'active' : ''}
            onClick={() => setDays(30)}
          >
            Last 30 days
          </button>
          <button type="button" onClick={refresh} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="health-error">{error}</div>}

      {loading && !pipelineHealth ? (
        <div className="health-loading">Loading…</div>
      ) : (
        <>
          <WeekVerdict data={pipelineHealth} poolSummary={poolSummary} days={days} />
          <WhoIsLanding characters={pipelineHealth?.characters} days={days} />
        </>
      )}

      <Pool apiUrl={apiUrl} embedded defaultSection="mining" />

      <details className="health-details">
        <summary>Pipeline details</summary>
        <div className="health-details-body">
          {pipelineHealth ? (
            <PipelineHealthPanels data={pipelineHealth} />
          ) : (
            <div className="health-empty">Load health data to see pipeline details.</div>
          )}
        </div>
      </details>
    </div>
  );
};

export default HealthTab;
